import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import type { Group, ThemeClasses } from "../types";
import { getStatusHumanLabel } from "../../../../../../utils/getStatusEntity";
import {
  getTimeAgo,
  type TimeTranslations,
} from "../../../../../../utils/getDate";
import useIsMonitor from "../../../../../../hooks/useIsMonitor";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";
import { DENSE_ROW, ICON_BTN, ICON_BTN_DANGER, STATUS_CHIP } from "../../../../shared";
interface GroupListRowProps {
  group: Group;
  themeClasses: ThemeClasses;
  onEdit: (group: Group) => void;
  onDelete: (group: Group) => void;
  onViewDetails: (group: Group) => void;
}

export function GroupListRow({
  group,
  themeClasses,
  onEdit,
  onDelete,
  onViewDetails,
}: GroupListRowProps) {
  const { t } = useTranslation("adminSections");
  const { t: tLite } = useTranslation("lite");
  const { isMonitor, isReadOnly } = useIsMonitor();
  const disableCRUD = useDisableCRUD();

  const timeTranslations: TimeTranslations = useMemo(
    () => ({
      noData: tLite("time.noData"),
      rightNow: tLite("time.rightNow"),
      min: tLite("time.min"),
      hour: tLite("time.hour"),
      hours: tLite("time.hours"),
      day: tLite("time.day"),
      days: tLite("time.days"),
      month: tLite("time.month"),
      months: tLite("time.months"),
      and: tLite("time.and"),
    }),
    [tLite],
  );
  const neutralBtn = `${ICON_BTN} ${themeClasses.buttonHover} ${themeClasses.buttonText} ${themeClasses.buttonTextHover}`;
  const devicesCount = group.devicesCount ?? group.devices_ids?.length ?? 0;
  return (
    <div className={`${DENSE_ROW} ${themeClasses.cardBg} ${themeClasses.cardBorder}`}>
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 bg-primary-500/10 text-primary-500">
        <i className="ri-group-line text-base"></i>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className={`text-sm font-semibold ${themeClasses.text} truncate`} title={group.name}>
            {group.name}
          </h3>
          <span className={`${STATUS_CHIP} ${themeClasses.textSecondary} border ${themeClasses.cardBorder}`}>
            {group?.status?.name
              ? getStatusHumanLabel(group.status.name, t)
              : t("groups.fields.noStatus")}
          </span>
        </div>
        <p className={`text-xs ${themeClasses.textSecondary} truncate`}>
          {group.description || t("groups.fields.noDescription")}
        </p>
      </div>

      {/* Devices count */}
      <div className="hidden md:block w-28 shrink-0 text-sm whitespace-nowrap">
        <span className={`font-medium tabular-nums ${themeClasses.text}`}>{devicesCount}</span>
        <span className={`text-xs ${themeClasses.textSecondary} ml-1`}>
          {t("groups.fields.devicesCountPlural")}
        </span>
      </div>

      {/* Updated */}
      <div className={`hidden lg:block w-28 shrink-0 text-xs truncate ${themeClasses.textSecondary}`}>
        {getTimeAgo(group.updatedAt?._seconds ?? 0, timeTranslations)}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-0.5 shrink-0">
        <button
          onClick={() => onViewDetails(group)}
          className={neutralBtn}
          aria-label={t("groups.actions.details")}
          title={t("groups.actions.details")}
        >
          <i className="ri-eye-line"></i>
        </button>
        {!isMonitor && !isReadOnly && (
          <>
            <CRUDTooltip>
              <button
                onClick={() => onEdit(group)}
                className={neutralBtn}
                aria-label={t("groups.actions.edit")}
                title={t("groups.actions.edit")}
                disabled={disableCRUD}
              >
                <i className="ri-pencil-line"></i>
              </button>
            </CRUDTooltip>
            <CRUDTooltip>
              <button
                onClick={() => onDelete(group)}
                className={ICON_BTN_DANGER}
                aria-label={t("groups.actions.delete")}
                title={t("groups.actions.delete")}
                disabled={disableCRUD}
              >
                <i className="ri-delete-bin-line"></i>
              </button>
            </CRUDTooltip>
          </>
        )}
      </div>
    </div>
  );
}
